import { useState, type FormEvent } from "react";
import { de, dayLabel, type Recipe } from "@ainkauf/core";
import { RecipeCover } from "./RecipeCover";
import { RecipeOrigin } from "./RecipeOrigin";

/**
 * Was kommt an diesem Tag auf den Tisch.
 *
 * Zwei Wege nebeneinander: ein Rezept aus der Sammlung oder ein freier Text.
 * Nicht jedes Abendessen ist ein Rezept, und "Reste" muss man eintragen
 * können, ohne dafür ein Rezept anzulegen.
 */
export function MealSheet({
  day,
  recipes,
  onSave,
  onCancel,
}: {
  day: string;
  recipes: readonly Recipe[];
  onSave: (inhalt: { recipeId?: string | null; note?: string | null; servings?: number | null }) => Promise<void>;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [personen, setPersonen] = useState("");
  const [busy, setBusy] = useState(false);

  const anzahl = () => {
    const zahl = Number.parseInt(personen, 10);
    return Number.isFinite(zahl) && zahl > 0 ? zahl : null;
  };

  async function speichere(inhalt: { recipeId?: string | null; note?: string | null }) {
    if (busy) return;
    setBusy(true);
    await onSave({ ...inhalt, servings: anzahl() });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void speichere({ note: text });
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={onCancel} role="presentation">
      <form
        onSubmit={submit}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-label={de.week.addMealFor(dayLabel(day))}
        data-testid="meal-sheet"
        className="flex max-h-[85dvh] flex-col gap-4 overflow-y-auto rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.week.addMealFor(dayLabel(day))}</h2>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.week.servings}
          <input
            value={personen}
            onChange={(event) => {
              setPersonen(event.target.value);
            }}
            inputMode="numeric"
            placeholder="2"
            data-testid="meal-servings"
            className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        {recipes.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-ink-2">{de.week.chooseRecipe}</h3>
            <ul className="flex flex-col gap-1.5">
              {recipes.map((rezept) => (
                <li key={rezept.id}>
                  <button
                    type="button"
                    onClick={() => void speichere({ recipeId: rezept.id })}
                    data-testid="meal-recipe"
                    data-name={rezept.name}
                    data-source={rezept.source}
                    className="flex min-h-[60px] w-full items-center gap-3 rounded-[var(--radius-lg)] bg-card px-3 text-left shadow-[var(--shadow-card)] transition-transform active:scale-[0.99]"
                  >
                    <RecipeCover recipe={rezept} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{rezept.name}</span>
                      <RecipeOrigin recipe={rezept} />
                    </span>
                    <span className="num shrink-0 text-sm text-ink-2">{de.week.servingsOf(rezept.servings)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.week.freeText}
          <input
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            placeholder={de.week.freeTextPlaceholder}
            data-testid="meal-note"
            className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[52px] flex-1 rounded-[var(--radius-lg)] bg-paper-2 font-semibold text-ink-2"
          >
            {de.common.cancel}
          </button>
          <button
            type="submit"
            disabled={busy || text.trim().length === 0}
            data-testid="meal-save"
            className="min-h-[52px] flex-1 rounded-[var(--radius-lg)] bg-accent font-display text-lg font-extrabold text-white disabled:opacity-40"
          >
            {de.common.save}
          </button>
        </div>
      </form>
    </div>
  );
}
